import { log } from '@axa-fr-axion/log';

const jsonToObjectMapper = (value) => value.json();
const objectToJsonMapper = (obj) => JSON.stringify(obj);

class RestError extends Error {
  constructor(response) {
    super(`${response.status} (${response.statusText})`);
    this.response = response;
  }
  get status() {
    return this.response.status;
  }
  get statusText() {
    return this.response.statusText;
  }
  async body() {
    try {
      return await jsonToObjectMapper(this.response);
    } catch (err) {
      log.error("Error while reading response error", err);
      return null;
    }
  }
}
const successfulResponseToObject = async (response) => {
  if (response.ok) {
    try {
      return await jsonToObjectMapper(response);
    } catch (err) {
      return {};
    }
  }
  throw new RestError(response);
};
const isClientError = (status) => {
  const BAD_REQUEST = 400;
  const INTERNAL_SERVER_ERROR = 500;
  const NOT_ACCEPTABLE = 406;
  return status >= BAD_REQUEST && status < INTERNAL_SERVER_ERROR && status !== NOT_ACCEPTABLE;
};

const FAILURE_COUNT_MAX = 3;
const isFunctionalError = (error, reasons = [
  "Rejet Sec",
  "Rejet VTC",
  "cnf",
  "hm",
  "HORS_LIMITE",
  "HORS_CIBLE"
], funnels = [
  "REJET-SEC",
  "HORS-CIBLE",
  "HORS-LIMITE",
  "HORS-METROPOLE",
  "FIABILITE",
  "DETENTEUR",
  "PERSONALISATION",
  "HORS-CIBLE-PRODUIT"
]) => error?.status?.code === "400" && (reasons.includes(error?.status?.reason) || funnels.includes(error?.status?.funnel));
const reactQueryRetry = (failureCount, error) => {
  const { status } = error;
  if (isClientError(status)) {
    return false;
  }
  return failureCount < FAILURE_COUNT_MAX;
};

let uuid;
const getHeaderCorrelationId = (name) => {
  if (!Boolean(uuid)) {
    uuid = crypto?.randomUUID();
  }
  return {
    "axa-feature": `id=${uuid},name=${name}`
  };
};

const MIN_RESPONSE_TIME = 100;
const MAX_RESPONSE_TIME = 2e3;
const defaultOptions = {
  min: MIN_RESPONSE_TIME,
  max: MAX_RESPONSE_TIME,
  disabled: false
};
const computeRandomDelay = (opts = {}) => {
  const { min, max, disabled } = {
    ...defaultOptions,
    ...opts
  };
  if (disabled) {
    return min;
  }
  return Math.floor(Math.random() * (max - min) + min);
};

const PATH_SEPARATOR = "/";
const sanitizePath = (path) => {
  if (!path?.length) {
    return "";
  }
  let sanitizedPath = path;
  if (sanitizedPath.startsWith(PATH_SEPARATOR)) {
    sanitizedPath = sanitizedPath.substring(1);
  }
  if (sanitizedPath.endsWith(PATH_SEPARATOR)) {
    sanitizedPath = sanitizedPath.substring(0, sanitizedPath.length - 1);
  }
  return sanitizedPath;
};
const buildPaths = (paths) => paths.filter(Boolean).map(sanitizePath).join(PATH_SEPARATOR);
const sanitizeValue = (value) => value !== null && value !== void 0 && value.toString() || "";
const prepareQueryParams = (params, forceEmptyParams = false) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).filter(([, value]) => value !== null && value !== void 0 || forceEmptyParams).forEach(([key, value]) => searchParams.set(key, sanitizeValue(value)));
  return searchParams.toString();
};
const buildQueryParams = (queryParams, forceEmptyParams = false) => {
  if (!queryParams) {
    return "";
  }
  const query = prepareQueryParams(queryParams, forceEmptyParams);
  return Boolean(query.length) ? "?" + query : "";
};
const buildUrl = (paths, queryParams, forceEmptyParams = false) => `${buildPaths(paths)}${buildQueryParams(queryParams, forceEmptyParams)}`;

export { MAX_RESPONSE_TIME, MIN_RESPONSE_TIME, RestError, buildUrl, computeRandomDelay, getHeaderCorrelationId, isClientError, isFunctionalError, jsonToObjectMapper, objectToJsonMapper, prepareQueryParams, reactQueryRetry, sanitizeValue, successfulResponseToObject };
